import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { toast } from 'sonner';

// Check if running as native app
export const isNativeApp = () => {
  return Capacitor.isNativePlatform();
};

// Initialize native push notifications
export const initializeNativePush = async (onTokenReceived) => {
  if (!isNativeApp()) {
    console.log('Not a native app, skipping native push initialization');
    return false;
  }

  try {
    // Request permission
    let permStatus = await PushNotifications.checkPermissions();
    
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      console.log('Push notification permission not granted');
      return false;
    }

    // Register for push notifications
    await PushNotifications.register();

    // Listen for registration success
    PushNotifications.addListener('registration', (token) => {
      console.log('Native push registration success, token:', token.value);
      if (onTokenReceived) {
        onTokenReceived(token.value);
      }
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (err) => {
      console.error('Native push registration error:', err.error);
      toast.error('Failed to register for notifications');
    });

    // Listen for push notifications received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      
      // Show in-app notification
      toast.success(notification.title || 'New Notification', {
        description: notification.body
      });
    });

    // Listen for push notification action (user tapped notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('Push notification action performed:', notification);
      
      // Handle notification tap - navigate to relevant screen
      const data = notification.notification.data;
      if (data?.propertyId) {
        // Navigate to property or dashboard
        window.location.href = '/dashboard';
      }
    });

    console.log('Native push notifications initialized successfully');
    return true;

  } catch (error) {
    console.error('Failed to initialize native push:', error);
    return false;
  }
};

// Remove all listeners (call on logout)
export const removeNativePushListeners = async () => {
  if (!isNativeApp()) return;
  
  try {
    await PushNotifications.removeAllListeners();
    console.log('Native push listeners removed');
  } catch (error) {
    console.error('Failed to remove push listeners:', error);
  }
};

// Get current permission status
export const getNativePushPermissionStatus = async () => {
  if (!isNativeApp()) return 'web';
  
  try {
    const status = await PushNotifications.checkPermissions();
    return status.receive;
  } catch (error) {
    console.error('Failed to check push permissions:', error);
    return 'denied';
  }
};
